import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { OntarioObstetricalFilePage, OntarioObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/OntarioObstetricalFilePage";
import { AddressHelper } from "@/Patient/Helpers/AddressHelper";
import { ObstetricalHistoryItem } from "@/Patient/Models/ObstetricalFile/ObstetricalHistoryItem";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalPatientPartnerInformation } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/OntarioObstetricalPatientPartnerInformation";
import { OntarioObstetricalFileMedicalHistory } from "./Subcomponent/OntarioObstetricalFileMedicalHistory";
import { OntarioObstetricalHistory } from "./Subcomponent/OntarioObstetricalFileObstetricalHistory";
import { OntarioObstetricalFilePregnancySummary } from "./Subcomponent/OntarioObstetricalFilePregnancySummary";

class OntarioObstetricalFileOpr1 extends OntarioObstetricalFilePage<OntarioObstetricalFilePageState> {
    private readonly componentId = "ontario-obstetrical-file-OPR1";

    protected initState(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): void {
        this.mutateState(
            s => s.form = new OntarioObstetricalFileForm(props.form),
            () => !this.state.form.Opr3.SubsequentVisits.any() && this.addSubsequentVisitRow(props)
        );
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="inner-border">
                <div className="section">
                    <div className="row">
                        {this.renderPersonalInfoSectionTop()}
                    </div>
                </div>
                <OntarioObstetricalFilePregnancySummary
                    {...this.getSubcomponentBaseProps()}
                    renderFinalEstimatedBirthDate={this.renderFinalEstimatedBirthDate("pregnancySummaryFinalEDB", "col-md-4")}
                    handleObstetricalCountersUpdate={() => this.handleObstetricalCountersUpdate()}
                    patient={this.props.patient}
                    contributionId={this.props.contributionId}
                />
                <div className="section border border-top pregnancysummary">
                    <div className="row">
                        <div className="col-md-12">
                            <p className="text-bold f16px">{this.getString("AntecedentsObstetriquesLabel")}</p>
                        </div>
                    </div>
                    <OntarioObstetricalHistory
                        readonly={this.props.readonly}
                        unit={this.state.form.Opr1.ObstetricalHistoryMassUnit}
                        onMassUnitChange={(unit: WeightUnit, pregnancies: ObstetricalHistoryItem[]) => {
                            this.mutateModelStateWithFormUpdate(s => {
                                s.form.Opr1.ObstetricalHistory = pregnancies;
                                s.form.Opr1.ObstetricalHistoryMassUnit = unit;
                            });
                        }}
                        updatePreviousPregnancies={(previousPregnancies: ObstetricalHistoryItem[], triggerSave: boolean) => {
                            triggerSave
                                ? this.mutateModelStateWithFormUpdate(s => s.form.Opr1.ObstetricalHistory = previousPregnancies)
                                : this.mutateModelState(s => s.form.Opr1.ObstetricalHistory = previousPregnancies);
                        }}
                        previousPregnanciesRows={this.props.form.Opr1.ObstetricalHistory}
                    />
                </div>
                <OntarioObstetricalFileMedicalHistory {...this.getSubcomponentBaseProps()} />
                {this.renderFooter()}
            </div>
        );
    }

    private renderPersonalInfoSectionTop(): JSX.Element {
        const personalInfo = this.state.form.Opr1.PersonalInformation;
        const address = this.props.patient.Addresses?.firstPreferredOrDefault(p => p.IsPreferred);

        const parsedStreetAddress = AddressHelper.parseStreetAddress(address?.StreetAddress);

        const streetAddress = parsedStreetAddress
            ? [parsedStreetAddress.civicNumber, parsedStreetAddress.streetName].filter(p => p?.trim()).join(" ")
            : address?.StreetAddress;

        if (!personalInfo)
            return null;

        const spouseInfo = this.state.form.Opr1.PersonalInformation.PartnerInformation;
        const onSpouseInfoUpdate = (mutation: (state: OntarioObstetricalPatientPartnerInformation) => void): void => this.mutateModelState(s => mutation(s.form.Opr1.PersonalInformation.PartnerInformation));
        const preferredPhoneNumber = PhoneNumber.GetPrimaryPhoneNumber(this.props.patient?.Phones);
        const common = this.state.form.Common;

        return (
            <div className="col-md-12">
                <div className="row row-form">
                    {this.renderLastName("col-md-3")}
                    {this.renderFirstName("col-md-3")}
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "ObstetricalFile_AddressStreetNumberStreetNameLabel",
                                <span className="field-text">{streetAddress?.substr(0, 41)}</span>,
                                "col-md-6"
                            )}
                            {this.renderField(
                                "ApptSuiteUnitNumberLabel",
                                <span className="field-text">{parsedStreetAddress?.suite?.substr(0, 13) ?? ""}</span>,
                                "col-md-3"
                            )}
                            {this.renderField(
                                "BuzzerNoLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.BuzzerNumber,
                                    onChange: value => this.mutateModelState(s => s.form.Opr1.PersonalInformation.BuzzerNumber = value),
                                    maxLength: 11,
                                    tabIndex: 1,
                                    onKeyDown: event => {
                                        if (event.key === "Tab" && event.shiftKey)
                                            event.preventDefault();
                                    }
                                }),
                                "col-md-3"
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "CityOrTownLabel",
                                <span className="field-text">{address?.City}</span>,
                                "col-md-6"
                            )}
                            {this.renderField(
                                "ProvinceLabel",
                                <span className="field-text">{cacheStore.GetLookupById(address?.StateLookupId)?.GetValue(this.locale)?.substr(0, 17)}</span>,
                                "col-md-3"
                            )}
                            {this.renderField(
                                "lblPostalCode",
                                <span className="field-text">{address?.ZipCode}</span>,
                                "col-md-3"
                            )}
                        </div>
                    </div>
                    <div className="col-md-6 partner-section">
                        <div className="border top" style={{ top: 0 }} />
                        <div className="border left" />
                        <div className="row row-form">
                            {this.renderField(
                                "PartnersFirstNameLabel",
                                this.controlRenderer.renderTextbox({
                                    value: spouseInfo.FirstName,
                                    onChange: firstName => onSpouseInfoUpdate(p => p.FirstName = firstName),
                                    maxLength: 28,
                                    tabIndex: 2
                                }),
                                "col-md-6"
                            )}
                            {this.renderField(
                                "PartnersLastNameLabel",
                                this.controlRenderer.renderTextbox({
                                    value: spouseInfo.LastName,
                                    onChange: lastName => onSpouseInfoUpdate(p => p.LastName = lastName),
                                    maxLength: 29,
                                    tabIndex: 2
                                }),
                                "col-md-6"
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "ObstetricalFile_ContactPreferredLabel",
                                <span className="field-text">{preferredPhoneNumber ? PhoneNumber.stringFormattedPhone(preferredPhoneNumber) : ""}</span>,
                                "col-md-3"
                            )}
                            {this.renderRadioButtonField(
                                "LeaveMessageLabel",
                                this.controlRenderer.renderInlineYesNoRadio(
                                    personalInfo.LeaveMessage,
                                    value => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.PersonalInformation.LeaveMessage = value),
                                    "personalInformationLeaveMessage",
                                    "radio-btn-min-height",
                                    1
                                ),
                                "col-md-3"
                            )}
                            {this.renderField(
                                "ObstetricalFile_ContactAlternateEmailLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.AlternateContact,
                                    onChange: alternateContact => this.mutateModelState(s => s.form.Opr1.PersonalInformation.AlternateContact = alternateContact),
                                    maxLength: 21,
                                    tabIndex: 1
                                }),
                                "col-md-6"
                            )}
                        </div>
                    </div>
                    <div className="col-md-6 partner-section">
                        <div className="border bottom" />
                        <div className="border left" style={{ bottom: 0 }} />
                        <div className="row row-form">
                            {this.renderField(
                                "PartnersOccupationLabel",
                                this.controlRenderer.renderTextbox({
                                    value: spouseInfo.Occupation,
                                    onChange: occupation => onSpouseInfoUpdate(p => p.Occupation = occupation),
                                    maxLength: 28,
                                    tabIndex: 2
                                }),
                                "col-md-6"
                            )}
                            {this.renderField(
                                "PartnersEducationLabel",
                                this.controlRenderer.renderAutoCompleteTextbox({
                                    value: spouseInfo.EducationLevel,
                                    autoCompleteItems: this.getEducationLevelOptions(),
                                    onChange: education => onSpouseInfoUpdate(p => p.EducationLevel = education),
                                    onAutoCompleteSelected: (code, _, value) => onSpouseInfoUpdate(p => p.EducationLevel = value),
                                    maxLength: 23,
                                    disableMatchAutoSorting: true,
                                    tabIndex: 2
                                }),
                                "col-md-4"
                            )}
                            {this.renderField(
                                "AgeLabel",
                                this.controlRenderer.renderTextbox({
                                    value: spouseInfo.Age?.toString(),
                                    onChange: age => onSpouseInfoUpdate(p => p.Age = age ? parseFloat(age) : null),
                                    isNumeric: true,
                                    maxLength: 5,
                                    tabIndex: 2,
                                    id: "PartnersAgeField",
                                    onKeyDown: event => {
                                        if (event.key === "Tab" && !event.shiftKey) {
                                            event.preventDefault();
                                            window.document.getElementById("AgeAtEstimatedDateofBirthField").focus();
                                        }
                                    }
                                }),
                                "col-md-2"
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "DateofBirthLabel",
                                <span className="field-text">{DateHelper.formatIsoDateOnly(this.props.patient.IndividualResponse.DateOfBirth)}</span>,
                                "col-md-3"
                            )}
                            {this.renderField(
                                "ObstetricalFile_AgeAtEstimatedDateOfBirthAbbrevLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.AgeAtEstimatedDateOfBirth?.toString(),
                                    onChange: age => this.mutateModelState(s => s.form.Opr1.PersonalInformation.AgeAtEstimatedDateOfBirth = age ? parseFloat(age) : null),
                                    isNumeric: true,
                                    maxLength: 8,
                                    id: "AgeAtEstimatedDateofBirthField",
                                    onKeyDown: event => {
                                        if (event.key === "Tab" && event.shiftKey) {
                                            event.preventDefault();
                                            window.document.getElementById("PartnersAgeField").focus();
                                        }
                                    }
                                }),
                                "col-md-3"
                            )}
                            {this.renderField(
                                "LanguageLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.Language,
                                    onChange: language => this.mutateModelState(s => s.form.Opr1.PersonalInformation.Language = language),
                                    maxLength: 22
                                }),
                                "col-md-3"
                            )}
                            {this.renderRadioButtonField(
                                "InterpreterRequiredLabel",
                                this.controlRenderer.renderInlineYesNoRadio(
                                    personalInfo.InterpreterRequired,
                                    value => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.PersonalInformation.InterpreterRequired = value),
                                    "personalInformationInterpreterRequired",
                                    "radio-btn-min-height"
                                ),
                                "col-md-3"
                            )}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "OccupationLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.Occupation,
                                    onChange: occupation => this.mutateModelState(s => s.form.Opr1.PersonalInformation.Occupation = occupation),
                                    maxLength: 14
                                }),
                                "col-md-3"
                            )}
                            {this.renderField(
                                "EducationLabel",
                                this.controlRenderer.renderAutoCompleteTextbox({
                                    value: personalInfo.EducationLevel,
                                    autoCompleteItems: this.getEducationLevelOptions(),
                                    onChange: educationLevel => this.mutateModelState(s => s.form.Opr1.PersonalInformation.EducationLevel = educationLevel),
                                    onAutoCompleteSelected: (code, _, value) => this.mutateModelState(s => s.form.Opr1.PersonalInformation.EducationLevel = value),
                                    maxLength: 13,
                                    disableMatchAutoSorting: true
                                }),
                                "col-md-3"
                            )}
                            {this.renderField(
                                "RelationshipStatusLabel",
                                this.controlRenderer.renderAutoCompleteTextbox({
                                    value: personalInfo.RelationshipStatus,
                                    autoCompleteItems: cacheStore.GetMaritalStatusLookups().map(p => p.ToSearchableItemModel(this.locale)),
                                    onChange: status => this.mutateModelState(s => s.form.Opr1.PersonalInformation.RelationshipStatus = status),
                                    onAutoCompleteSelected: (code, model, value) => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.PersonalInformation.RelationshipStatus = value),
                                    maxLength: 16
                                }),
                                "col-md-3"
                            )}
                            {this.renderField(
                                "SexualOrientationLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.SexualOrientation,
                                    onChange: sexualOrientation => this.mutateModelState(s => s.form.Opr1.PersonalInformation.SexualOrientation = sexualOrientation),
                                    maxLength: 13
                                }),
                                "col-md-3"
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "OntarioHealthInsurancePlanAbbrevNumberLabel",
                                <span className="field-text">{this.props.patient.HealthCards.firstOrDefault(p => p.ProvinceLookupId === cacheStore.GetLookupIdForProvince(CanadaState.Ontario))?.Number ?? ""}</span>,
                                "col-md-4"
                            )}
                            {this.renderField(
                                "PatientFileNumberLabel",
                                this.controlRenderer.renderTextbox({
                                    value: personalInfo.PatientFileNumber,
                                    onChange: fileNumber => this.mutateModelState(s => s.form.Opr1.PersonalInformation.PatientFileNumber = fileNumber),
                                    maxLength: 20
                                }),
                                "col-md-5"
                            )}
                            {this.renderRadioButtonField(
                                "DisabilityRequiringAccommodation",
                                this.controlRenderer.renderInlineYesNoRadio(
                                    personalInfo.DoesDisabilityRequireAccommodation,
                                    value => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.PersonalInformation.DoesDisabilityRequireAccommodation = value),
                                    "personalInformationDoesDisabilityRequireAccommodation",
                                    "radio-btn-min-height"
                                ),
                                "col-md-3"
                            )}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="row row-form">
                            {this.renderField(
                                "ObstetricalFile_PlannedPlaceOfBirthLabel",
                                this.controlRenderer.renderAutoCompleteTextbox({
                                    value: common.PlannedPlaceOfBirth,
                                    autoCompleteItems: this.getPlannedPlaceOfBirthOptions(),
                                    onChange: location => this.mutateModelState(s => s.form.Common.PlannedPlaceOfBirth = location),
                                    onAutoCompleteSelected: (code, _, value) => this.mutateModelState(s => s.form.Common.PlannedPlaceOfBirth = value),
                                    maxLength: 28,
                                    disableMatchAutoSorting: true
                                }),
                                "col-md-6"
                            )}
                            {this.renderPlannedBirthAttendant("col-md-6")}
                        </div>
                    </div>
                </div>
                {this.renderNewBornCareProvider()}
                {this.renderAllergiesAndMedications()}
            </div>
        );
    }

    private getEducationLevelOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("NoneWordLabel"), "NoneWordLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_ApprenticeshipLabel"), "EducationLevel_ApprenticeshipLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_CollegeLabel"), "EducationLevel_CollegeLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_HighschoolLabel"), "EducationLevel_HighschoolLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_UniversityLabel"), "EducationLevel_UniversityLabel")
        ];
    }

    private getPlannedPlaceOfBirthOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("ObstetricalFile_PlannedBirthLocationOption_HospitalLabel"), "Hospital"),
            new SearchableItemModelT(this.getString("Home2Label"), "Home"),
            new SearchableItemModelT(this.getString("ObstetricalFile_PlannedBirthLocationOption_BirthCentreLabel"), "BirthCentre")
        ];
    }

    private renderNewBornCareProvider(): JSX.Element {
        return (
            <div className="row row-form">
                {this.renderNewBornFields("col-md-6")}
                {this.renderFamilyPhysicianOrPrimaryCareProvider("col-md-6")}
            </div>
        );
    }

    private renderAllergiesAndMedications(): JSX.Element {
        return (
            <div className="row row-form">
                {this.renderAllergiesOrSensitivites("col-md-6")}
                {this.renderMedications("col-md-6")}
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        const signatureBlock = this.state.form.Opr1.SignaturesBlock;

        return (
            <div className="section border border-top">
                <div className="row">
                    {this.renderField(
                        "CompletedByLabel",
                        this.controlRenderer.renderTextbox({
                            value: signatureBlock.CompletedByValue,
                            onChange: value => this.mutateModelState(s => s.form.Opr1.SignaturesBlock.CompletedByValue = value, null, true),
                            maxLength: 49
                        }),
                        "col-xs-6"
                    )}
                    {this.renderField(
                        "ReviewedByDLabel",
                        this.controlRenderer.renderTextbox({
                            value: signatureBlock.ReviewedBy,
                            onChange: value => this.mutateModelState(s => s.form.Opr1.SignaturesBlock.ReviewedBy = value, null, true),
                            maxLength: 50
                        }),
                        "col-xs-6"
                    )}
                </div>
                <div className="row">
                    {this.renderField(
                        "SignatureLabel",
                        this.controlRenderer.renderTextbox({
                            value: signatureBlock.Signature,
                            onChange: value => this.mutateModelState(s => s.form.Opr1.SignaturesBlock.Signature = value, null, true),
                            maxLength: 28
                        }),
                        "col-xs-4"
                    )}
                    {this.renderField(
                        "DateLabel",
                        this.controlRenderer.renderDateSelector({
                            value: signatureBlock.SignatureDate,
                            onChange: value => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.SignaturesBlock.SignatureDate = value),
                            id: "Opr1SignatureDate",
                            className: "width-100"
                        }),
                        "col-xs-2"
                    )}
                    {this.renderField(
                        "MrpSignatureLabel",
                        this.controlRenderer.renderTextbox({
                            value: signatureBlock.MostResponsibleProviderSignature,
                            onChange: value => this.mutateModelState(s => s.form.Opr1.SignaturesBlock.MostResponsibleProviderSignature = value, null, true),
                            maxLength: 24
                        }),
                        "col-xs-4"
                    )}
                    {this.renderField(
                        "DateLabel",
                        this.controlRenderer.renderDateSelector({
                            value: signatureBlock.MostResponsibleProviderSignatureDate,
                            onChange: value => this.mutateModelStateWithFormUpdate(s => s.form.Opr1.SignaturesBlock.MostResponsibleProviderSignatureDate = value),
                            id: "Opr1MrpSignatureDate",
                            className: "width-100"
                        }),
                        "col-xs-2"
                    )}
                </div>
            </div>
        );
    }

    protected mutateModelState(mutation: (state: OntarioObstetricalFilePageState) => void, then?: () => void, ignoreSignature?: boolean): void {
        super.mutateModelState(
            s => {
                mutation(s);
                this.manageSignature(s, ignoreSignature);
            },
            then
        );
    }

    private manageSignature(state: OntarioObstetricalFilePageState, ignoreSignature?: boolean): void {
        const currentUser = sessionStore.getCurrentEmployee();
        if (state.form.Opr1.SignaturesBlock.CompletedByUserContactFacetIds?.any(id => id === currentUser.ContactFacetId))
            return;

        if (!ignoreSignature) {
            state.form.Opr1.SignaturesBlock.CompletedByUserContactFacetIds.push(currentUser.ContactFacetId);
            state.form.Opr1.SignaturesBlock.CompletedByValue = limitTextLength((`${state.form.Opr1.SignaturesBlock.CompletedByValue}${(!!state.form.Opr1.SignaturesBlock.CompletedByValue ? ", " : "")}${currentUser.nameWithPrefix()}`), 65);
        }
    }

    protected mutateModelStateWithFormUpdate(mutation: (state: OntarioObstetricalFilePageState) => void): void {
        super.mutateModelStateWithFormUpdate(state => {
            mutation(state);
            this.manageSignature(state);
        });
    }
}

export { OntarioObstetricalFileOpr1 };
